import {
  SceneCompare,
  StoryBlock,
  StoryBlockType,
  StoryContent,
  StoryScene,
  StorySceneAnimation,
} from "../../types/storyMap";

/**
 * `crypto.randomUUID` needs a secure context — it throws over plain HTTP,
 * which bites anyone testing a dev build by LAN IP. Same fallback shape as
 * AddEditTreatmentModal's generateSeriesId.
 */
const newId = (prefix: string): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/**
 * A fresh scene. Project stories start with the legacy "boundary" layer key
 * (the builder migrates to the entry model on the first edit); folder and
 * organisation-wide stories (`scoped`) have no implied project, so they start
 * with an empty entry list and the author adds each project's layers.
 */
export const newScene = (index: number, opts: { scoped?: boolean } = {}): StoryScene => ({
  id: newId("scene"),
  name: `Scene ${index + 1}`,
  kicker: "",
  transition: "fly",
  camera: null,
  layers: opts.scoped ? [] : ["boundary"],
  ...(opts.scoped ? { layerStack: [] } : {}),
  strategyId: null,
  simulationId: null,
  explorable: false,
  blocks: [],
  mode: "layers",
  date: null,
});

export const defaultSceneCompare = (): SceneCompare => ({
  before: { label: "BASELINE", strategyId: null, date: null, layers: [] },
  after: { label: "TREATED", strategyId: null, date: null, layers: [] },
});

/** One default animation entry — animation mode starts with a single entry. */
export const defaultSceneAnimation = (): StorySceneAnimation => ({
  id: newId("anim"),
  simulationId: null,
  partition: null,
  view: "single",
  percentile: 50,
  resolvedMember: null,
  metric: "ros",
  color: null,
  opacity: 0.85,
});

export const defaultStoryContent = (authorName?: string, scoped = false): StoryContent => ({
  version: 1,
  subtitle: "",
  authorName: authorName || "",
  authorRole: "",
  scenes: [newScene(0, { scoped })],
});

export const newBlock = (type: StoryBlockType): StoryBlock => {
  const id = newId("block");
  switch (type) {
    case "text":
      return { id, type, html: "" };
    case "heading":
      return { id, type, text: "" };
    case "metric":
      return { id, type, label: "", value: "", units: "", source: { kind: "manual" } };
    case "metrics_row":
      return {
        id,
        type,
        items: [
          { label: "", value: "", units: "", source: { kind: "manual" } },
          { label: "", value: "", units: "", source: { kind: "manual" } },
        ],
      };
    case "image":
      return { id, type, snapshotId: null, url: "", caption: "" };
  }
};
